import { FormEvent, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, Infinity, Zap, User, ShoppingCart, ArrowLeft } from 'lucide-react';
import GrainTexture from '@/components/GrainTexture';
import { SEO } from '@/components/SEO';
import { DEFAULT_OG_IMAGE } from '@/seo/siteConfig';

export default function KoopStrippenkaart() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGuestCheckout = async (event: FormEvent) => {
    event.preventDefault();

    if (!email) {
      toast.error('Vul een geldig e-mailadres in');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'guest',
          userEmail: email,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || 'Er ging iets mis bij het aanmaken van de betalingslink');
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Geen checkout URL ontvangen');
      }
    } catch (error) {
      console.error('Guest checkout error:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Er ging iets mis bij het aanmaken van de betalingslink. Probeer het opnieuw.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="3-strippen strippenkaart | €100"
        description="3 strippen voor deelname als representant bij organisatieopstellingen"
        canonical="https://organisatieopstellingen.com/koop-strippenkaart"
        ogImage={DEFAULT_OG_IMAGE}
        ogType="product"
        ogTitle="3-Strippen Strippenkaart - €100"
        ogDescription="3 strippen voor deelname als representant bij organisatieopstellingen"
        noindex
      />
      
      {/* Product Schema */}
      <meta property="product:price:amount" content="100.00" />
      <meta property="product:price:currency" content="EUR" />

      {/* Custom Luxury Header */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-6 overflow-hidden bg-[#854C73]">
        <div className="absolute inset-0 z-0">
          <GrainTexture opacity={0.2} />
          <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] border border-[#F9EBEF]/10 rounded-full translate-x-1/2 -translate-y-1/2 opacity-30"></div>
        </div>
        
        <div className="container mx-auto relative z-10 text-center">
          <Button variant="ghost" asChild className="mb-8 hover:bg-white/20 text-white hover:text-white border border-white/30 mx-auto flex w-fit px-6 py-2 h-auto cursor-pointer">
            <Link to="/">
              <ArrowLeft className="mr-2 w-4 h-4" /> Terug naar home
            </Link>
          </Button>
          
          <h1 className="font-serif text-3xl md:text-5xl text-[#F9EBEF] mb-6">
            Strippenkaart <span className="italic font-light font-serif text-[#F9EBEF]">Kopen</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-[#F9EBEF]/80 font-serif italic max-w-2xl mx-auto">
            Investeer in jezelf en je ontwikkeling.
          </p>
        </div>
      </section>

      <div className="animate-fade-in bg-[#FAEBEE] min-h-screen pb-24">
        <section className="py-12 px-4 md:px-6 relative z-30">
          <div className="container mx-auto max-w-6xl grid lg:grid-cols-12 gap-8 items-start">
            
            {/* Linkerkant (Informatie & Account) */}
            <div className="lg:col-span-7 space-y-6">
              
              {/* Wat krijg je? */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#fff]">
                <h3 className="font-serif text-2xl text-[#854C73] mb-6">Wat krijg je?</h3>
                <ul className="space-y-5">
                  <li className="flex items-start gap-4">
                    <div className="bg-[#FAEBEE] p-1.5 rounded-full shrink-0"><CheckCircle size={20} className="text-[#854C73]" /></div>
                    <span className="text-gray-700 text-lg">3 strippen voor deelname als representant</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="bg-[#FAEBEE] p-1.5 rounded-full shrink-0"><Infinity size={20} className="text-[#854C73]" /></div>
                    <span className="text-gray-700 text-lg">Geen vervaldatum: strippen blijven onbeperkt geldig</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="bg-[#FAEBEE] p-1.5 rounded-full shrink-0"><Zap size={20} className="text-[#854C73]" /></div>
                    <span className="text-gray-700 text-lg">Direct na betaling actief en klaar voor gebruik</span>
                  </li>
                </ul>
              </div>
              
              {/* Heb je al een account? */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#fff]">
                <h3 className="font-serif text-2xl text-[#6b4912] mb-4">Heb je al een account?</h3>
                <p className="text-gray-600 mb-8 text-lg">Log dan in om direct te kopen en je strippenkaart automatisch te laten toevoegen.</p>
                
                <div className="flex flex-col gap-4 mb-8">
                  <Button variant="default" className="w-full justify-center py-4 text-lg" asChild>
                    <Link to="/login">Log in</Link>
                  </Button>
                  <Button variant="outline" className="w-full justify-center py-4 text-lg bg-transparent hover:bg-[#FAEBEE]" asChild>
                    <Link to="/signup">Maak account aan</Link>
                  </Button>
                </div>
                <div className="bg-[#FAEBEE] p-6 rounded-xl flex gap-4 items-start border border-[#854C73]/10">
                  <User className="text-[#854C73] shrink-0 mt-1" size={24} />
                  <div>
                    <p className="font-bold text-[#854C73] text-base mb-1">Koppel achteraf</p>
                    <p className="text-sm text-gray-600 leading-relaxed">Koop je eerst als gast? Gebruik later dezelfde mail bij het inloggen. Je strippenkaart verschijnt dan automatisch in je account.</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Rechterkant (Besteloverzicht / Checkout) */}
            <div className="lg:col-span-5">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-[#854C73]/10 lg:sticky lg:top-24 transform transition-all hover:scale-[1.01] duration-500">
                {/* Header */}
                <div className="bg-[#854C73] p-5 text-center text-white relative overflow-hidden">
                  <GrainTexture opacity={0.2} />
                  <h3 className="font-serif text-xl flex items-center justify-center gap-3 relative z-10 tracking-wider">
                    <ShoppingCart size={22} /> STRIPPENKAART KOPEN
                  </h3>
                </div>
                
                <div className="p-6 md:p-8">
                  <h3 className="font-serif text-2xl text-[#6b4912] mb-6">Besteloverzicht</h3>
                  
                  {/* Product Item */}
                  <div className="relative border border-[#FAEBEE] bg-white rounded-xl p-4 mb-6 shadow-sm group">
                    {/* Badge */}
                    <div className="absolute -top-3 -right-3 bg-[#4ade80] text-white text-xs font-bold px-3 py-1 shadow-md rounded-md uppercase tracking-wider">
                      42% voordeliger dan een los ticket
                    </div>
                    
                    <div className="flex gap-4 items-start">
                      <div className="w-14 h-14 bg-[#FAEBEE] rounded-xl flex items-center justify-center text-[#854C73] shrink-0 group-hover:scale-110 transition-transform">
                        <ShoppingCart size={28} />
                      </div>
                      <div className="flex-grow">
                        <div className="flex justify-between items-start mb-1">
                          <h4 className="font-bold text-[#6b4912] text-lg">3 Strippen Strippenkaart</h4>
                        </div>
                        <p className="text-sm text-gray-500 mb-3 leading-snug">Geldig voor deelname als representant. Geen vervaldatum.</p>
                        <div className="flex justify-end items-end gap-3">
                          <span className="font-serif font-bold text-2xl text-[#854C73]">€ 130,00</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Totalen */}
                  <div className="border-t border-dashed border-gray-200 pt-4 space-y-3 mb-8">
                    <div className="flex justify-between text-gray-600 font-medium">
                      <span>Subtotaal</span>
                      <span>€ 107,44</span>
                    </div>
                    <div className="flex justify-between text-gray-600 font-medium">
                      <span>BTW (21%)</span>
                      <span>€ 22,56</span>
                    </div>
                    <div className="border-t border-gray-200 my-2"></div>
                    <div className="flex justify-between text-[#6b4912] font-serif font-bold text-2xl pt-2">
                      <span>Totaalbedrag</span>
                      <span>€ 130,00</span>
                    </div>
                  </div>
                  
                  {/* E-mail invoer */}
                  <form onSubmit={handleGuestCheckout}>
                    <div className="mb-6">
                      <label className="block text-sm text-gray-600 mb-2">E-mailadres voor bevestiging</label>
                      <input 
                        type="email" 
                        placeholder="jouw@email.nl" 
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full px-5 py-3 rounded-xl border border-gray-200 focus:border-[#854C73] focus:ring-4 focus:ring-[#854C73]/10 outline-none transition-all bg-white focus:bg-white text-lg"
                      />
                    </div>
                    
                    {/* Betaal Knop */}
                    <Button 
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 text-lg shadow-xl shadow-[#854C73]/20 mb-6 bg-[#854C73] hover:bg-[#703e60] group rounded-xl"
                    >
                      <span className="group-hover:scale-105 transition-transform flex items-center gap-2 font-serif uppercase tracking-wide">
                        <ShoppingCart size={20} /> {loading ? 'BEZIG MET LADEN...' : 'NU AFREKENEN'}
                      </span>
                    </Button>
                    
                    {/* Footer zekerheid */}
                    <div className="text-center flex items-center justify-center gap-2 text-[#557d68] text-sm font-medium">
                      <CheckCircle size={16} className="text-[#4ade80]" /> Betaal met Apple Pay of iDEAL
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

